import { useState } from 'react';
import { Modal, Input, Alert, Typography, Tag, Space } from 'antd';
import { proformaApi } from './proformaApi';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { notifySuccess, notifyError, parseApiError } from '../../../../utils/notify';

const { Text } = Typography;

// WO-3.1 — pre-cut Recall. A released order whose Work Order has NOT started
// cutting can be pulled back: this withdraws the Work Order and reopens the
// proforma to an editable draft so the sizes can be changed, then re-confirmed
// and re-released. Runs the L1 Approval-MFA step-up, POSTs the recall, and hands
// the caller the reopened proforma so it can navigate into the editor. If cutting
// has already started the backend refuses (WO_CUTTING_STARTED) and the user is
// pointed at a Change Order instead.
export default function RecallModal({ open, proforma, onClose, onDone }) {
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();
  const [reason, setReason] = useState('');
  const [applying, setApplying] = useState(false);

  const id = proforma && proforma.id;
  const proformaNo = proforma && proforma.proformaNo;

  async function apply() {
    setApplying(true);
    try {
      const result = await runWithApproval({
        operationKey: 'production.workorder.recall',
        resourceType: 'proforma',
        resourceId: id,
        operationLabel: 'Recall from production',
        resourceLabel: proformaNo,
        action: (challengeId) => proformaApi.recall(id, {
          reason: reason || null, approvalMfaChallengeId: challengeId
        })
      });
      notifySuccess('Recalled from production — the proforma is editable again.');
      setReason('');
      if (typeof onDone === 'function') onDone(result);
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;   // user closed the MFA modal
      notifyError('Could not recall this proforma', parseApiError(err)?.description);
    } finally {
      setApplying(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        title={<Space>↩ Recall from production <Tag color="gold">{proformaNo}</Tag></Space>}
        width={640}
        okText="Recall → withdraw Work Order, reopen to edit"
        okButtonProps={{ loading: applying, danger: true }}
        onOk={apply}
        onCancel={onClose}
        maskClosable={false}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Change sizes before cutting starts"
          description={
            <span>
              Nothing is cut yet, so this order can be pulled back cleanly. On Recall the
              released <b>Work Order is withdrawn</b> (cancelled) and this proforma reopens as an
              editable <b>Pending</b> draft. Change the sizes, <b>re-confirm</b>, and{' '}
              <b>re-release</b> a fresh Work Order. If cutting has already started, use{' '}
              <b>Raise Change Order</b> instead.
            </span>
          }
        />
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Reason (optional)</Text>
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer revised the sizes before production started"
            maxLength={500}
          />
        </div>
      </Modal>
      {ApprovalMfaModalSlot}
    </>
  );
}
